import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Mail, Send, Shield, AlertCircle, CheckCircle2, Plus, X, Eye, EyeOff, KeyRound,
} from 'lucide-react';

const PRESETS = {
  custom: { label: 'Personnalisé', host: '', port: 465, use_ssl: true },
  sellerie: { label: 'Sellerie Académie (SSL · 465)', host: 'mail.sellerie-academie.fr', port: 465, use_ssl: true },
  ovh_ssl: { label: 'OVH (SSL · 465)', host: 'ssl0.ovh.net', port: 465, use_ssl: true },
  ovh_tls: { label: 'OVH (STARTTLS · 587)', host: 'ssl0.ovh.net', port: 587, use_ssl: false },
  gmail: { label: 'Gmail (SSL · 465)', host: 'smtp.gmail.com', port: 465, use_ssl: true },
  brevo: { label: 'Brevo / Sendinblue (TLS · 587)', host: 'smtp-relay.brevo.com', port: 587, use_ssl: false },
  outlook: { label: 'Outlook (TLS · 587)', host: 'smtp.office365.com', port: 587, use_ssl: false },
};

const NOTIF_LABELS = {
  new_lead: { label: 'Nouveau lead programme PDF', desc: 'Admin notifié quand un prospect télécharge un programme.' },
  new_application: { label: 'Nouvelle candidature', desc: 'Admin notifié dès qu\'une candidature est déposée.' },
  application_received: { label: 'Confirmation au candidat', desc: 'Le candidat reçoit un accusé de réception de sa candidature.' },
  application_decision: { label: 'Décision candidature', desc: 'Le candidat est notifié quand sa candidature est acceptée ou refusée.' },
  programme_pdf_to_prospect: { label: 'Programme PDF au prospect', desc: 'Le prospect reçoit le programme en pièce jointe par email.' },
  commission_validated: { label: 'Commission validée', desc: 'L\'apporteur reçoit un email quand sa commission est validée.' },
};

export const SmtpSettingsAdmin = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [pwd, setPwd] = useState('');
  const [newRecipient, setNewRecipient] = useState('');
  const [logs, setLogs] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.getSmtpSettings(),
        api.listSmtpLogs(20).catch(() => []),
      ]);
      setData(s);
      setLogs(l);
    } catch (e) { toast.error('Erreur de chargement des paramètres SMTP'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return <div className="h-64 bg-white border border-[#1F2A4A]/10 rounded-3xl animate-pulse" />;
  }

  const update = (k, v) => setData({ ...data, [k]: v });
  const applyPreset = (key) => {
    const p = PRESETS[key];
    if (!p) return;
    setData({ ...data, host: p.host, port: p.port, use_ssl: p.use_ssl });
  };

  const addRecipient = () => {
    const v = newRecipient.trim();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error('Email invalide');
      return;
    }
    if ((data.admin_recipients || []).includes(v)) {
      toast.error('Déjà dans la liste');
      return;
    }
    update('admin_recipients', [...(data.admin_recipients || []), v]);
    setNewRecipient('');
  };
  const removeRecipient = (email) => {
    update('admin_recipients', data.admin_recipients.filter((e) => e !== email));
  };

  const save = async () => {
    if (!data.host || !data.from_email) {
      toast.error('Host SMTP et adresse d\'envoi obligatoires');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        enabled: data.enabled,
        host: data.host.trim(),
        port: parseInt(data.port) || 465,
        use_ssl: data.use_ssl,
        username: data.username.trim(),
        password: pwd || null,    // null = keep existing
        from_name: data.from_name,
        from_email: data.from_email.trim(),
        reply_to: data.reply_to || '',
        admin_recipients: data.admin_recipients || [],
        notifications: data.notifications,
      };
      await api.saveSmtpSettings(payload);
      toast.success('Configuration SMTP enregistrée');
      setPwd('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur d\'enregistrement');
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    if (!testEmail.trim()) return toast.error('Saisis une adresse de test');
    setTesting(true);
    try {
      await api.testSmtp(testEmail.trim());
      toast.success(`Email de test envoyé à ${testEmail}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec du test SMTP');
    } finally { setTesting(false); }
  };

  return (
    <div className="space-y-8" data-testid="smtp-settings-admin">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-2">PARAMÈTRES · SMTP</p>
        <h2 className="font-light text-3xl md:text-4xl text-[#1F2A4A] tracking-tight">
          Notifications &amp; <span className="font-serif italic text-[#C9B896]">envoi d'emails.</span>
        </h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Configure ton serveur SMTP. Les identifiants sont chiffrés au repos. Active uniquement quand le
          test d'envoi fonctionne pour éviter les retours en erreur côté utilisateur.
        </p>
      </div>

      {/* Master switch */}
      <div className={`rounded-2xl p-5 border flex items-center gap-4 ${data.enabled ? 'bg-emerald-50/60 border-emerald-200' : 'bg-amber-50/60 border-amber-200'}`}>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${data.enabled ? 'bg-emerald-100' : 'bg-amber-100'}`}>
          {data.enabled ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <AlertCircle className="w-5 h-5 text-amber-600" />}
        </div>
        <div className="flex-1">
          <p className="font-medium text-[#1F2A4A]">{data.enabled ? 'Envoi d\'emails activé' : 'Envoi d\'emails désactivé'}</p>
          <p className="text-xs text-muted-foreground">
            {data.enabled ? 'Les notifications sont envoyées via ton serveur SMTP.' : 'Aucun email ne sera envoyé tant que ce paramètre est inactif.'}
          </p>
        </div>
        <Switch checked={data.enabled} onCheckedChange={(v) => update('enabled', v)} data-testid="smtp-enabled-switch" />
      </div>

      {/* Connection */}
      <div className="bg-white border border-[#1F2A4A]/10 rounded-3xl p-6 md:p-8 space-y-6" data-testid="smtp-connection">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-heading text-xl text-[#1F2A4A] flex items-center gap-2"><Shield className="w-4 h-4 text-[#C9B896]" /> Serveur SMTP</h3>
          <Select onValueChange={applyPreset}>
            <SelectTrigger className="w-56" data-testid="smtp-preset">
              <SelectValue placeholder="Préréglage rapide…" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PRESETS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Host</Label>
            <Input value={data.host} onChange={(e) => update('host', e.target.value)} placeholder="ssl0.ovh.net" data-testid="smtp-host" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Port</Label>
            <Input type="number" value={data.port} onChange={(e) => update('port', e.target.value)} data-testid="smtp-port" />
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Utilisateur</Label>
            <Input value={data.username} onChange={(e) => update('username', e.target.value)} placeholder="exemple@sellerie-academie.fr" data-testid="smtp-username" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Chiffrement</Label>
            <Select value={data.use_ssl ? 'ssl' : 'tls'} onValueChange={(v) => update('use_ssl', v === 'ssl')}>
              <SelectTrigger data-testid="smtp-encryption"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ssl">SSL (port 465)</SelectItem>
                <SelectItem value="tls">STARTTLS (port 587)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#C9B896] flex items-center gap-2">
              <KeyRound className="w-3 h-3" /> Mot de passe
              {data.has_password && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Enregistré</Badge>}
            </Label>
            <div className="relative">
              <Input
                type={showPwd ? 'text' : 'password'}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder={data.has_password ? '••••••••• (laisse vide pour ne pas changer)' : 'Mot de passe SMTP'}
                data-testid="smtp-password"
                className="pr-10"
              />
              <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1F2A4A]/60 hover:text-[#1F2A4A]">
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground">Chiffré au repos avant stockage. Jamais renvoyé en clair par l'API.</p>
          </div>
        </div>
      </div>

      {/* Sender */}
      <div className="bg-white border border-[#1F2A4A]/10 rounded-3xl p-6 md:p-8 space-y-6">
        <h3 className="font-heading text-xl text-[#1F2A4A] flex items-center gap-2"><Mail className="w-4 h-4 text-[#C9B896]" /> Expéditeur &amp; destinataires</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Nom d'expéditeur (FROM)</Label>
            <Input value={data.from_name} onChange={(e) => update('from_name', e.target.value)} data-testid="smtp-from-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Adresse d'envoi</Label>
            <Input type="email" value={data.from_email} onChange={(e) => update('from_email', e.target.value)} placeholder="contact@sellerie-academie.fr" data-testid="smtp-from-email" />
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Reply-To (facultatif)</Label>
            <Input type="email" value={data.reply_to} onChange={(e) => update('reply_to', e.target.value)} placeholder="assistance.sellerie@gmail.com" data-testid="smtp-reply-to" />
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-[#C9B896] mb-2 block">
            Destinataires admin ({(data.admin_recipients || []).length})
          </Label>
          <p className="text-xs text-muted-foreground mb-3">Emails qui reçoivent les notifications administratives (nouveau lead, candidature, etc.)</p>
          <div className="flex gap-2 flex-wrap mb-3">
            {(data.admin_recipients || []).map((e) => (
              <span key={e} className="inline-flex items-center gap-1.5 bg-[#1F2A4A]/5 text-[#1F2A4A] rounded-full pl-3 pr-1 py-1 text-xs" data-testid={`recipient-${e}`}>
                {e}
                <button type="button" onClick={() => removeRecipient(e)} className="w-5 h-5 rounded-full hover:bg-red-100 flex items-center justify-center">
                  <X className="w-3 h-3 text-red-500" />
                </button>
              </span>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); addRecipient(); }} className="flex gap-2">
            <Input
              type="email"
              value={newRecipient}
              onChange={(e) => setNewRecipient(e.target.value)}
              placeholder="admin@sellerie-academie.fr"
              className="flex-1"
              data-testid="recipient-input"
            />
            <Button type="submit" variant="outline" className="rounded-full" data-testid="recipient-add">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </form>
        </div>
      </div>

      {/* Notification toggles */}
      <div className="bg-white border border-[#1F2A4A]/10 rounded-3xl p-6 md:p-8 space-y-4">
        <h3 className="font-heading text-xl text-[#1F2A4A]">Notifications activées</h3>
        <ul className="divide-y divide-[#1F2A4A]/8">
          {Object.entries(NOTIF_LABELS).map(([key, meta]) => (
            <li key={key} className="py-3 flex items-center gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-[#1F2A4A]">{meta.label}</p>
                <p className="text-xs text-muted-foreground">{meta.desc}</p>
              </div>
              <Switch
                checked={!!data.notifications?.[key]}
                onCheckedChange={(v) => update('notifications', { ...data.notifications, [key]: v })}
                data-testid={`notif-${key}`}
              />
            </li>
          ))}
        </ul>
      </div>

      {/* Action bar */}
      <div className="sticky bottom-4 z-10 bg-white border border-[#C9B896]/30 rounded-2xl shadow-xl shadow-[#1F2A4A]/10 p-4 flex items-center gap-3 flex-wrap">
        <Input
          type="email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          placeholder="Tester l'envoi vers…"
          className="flex-1 min-w-[200px] max-w-md"
          data-testid="smtp-test-email"
        />
        <Button
          onClick={runTest}
          disabled={testing || !data.has_password}
          variant="outline"
          className="rounded-full"
          data-testid="smtp-test-button"
        >
          <Send className="w-4 h-4 mr-2" /> {testing ? 'Envoi…' : 'Envoyer un test'}
        </Button>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#1F2A4A] text-white hover:bg-[#324166] rounded-full"
          data-testid="smtp-save-button"
        >
          {saving ? 'Enregistrement…' : 'Enregistrer la configuration'}
        </Button>
      </div>

      {/* Logs */}
      {logs.length > 0 && (
        <div className="bg-white border border-[#1F2A4A]/10 rounded-3xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[#1F2A4A]/8">
            <h3 className="font-heading text-xl text-[#1F2A4A]">Derniers envois</h3>
          </div>
          <table className="w-full">
            <thead>
              <tr className="bg-[#FAF7F0]/50 text-[10px] font-mono uppercase tracking-wider text-[#C9B896]">
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Destinataire</th>
                <th className="text-left p-3">Sujet</th>
                <th className="text-left p-3">Statut</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-[#1F2A4A]/8 text-sm">
                  <td className="p-3 text-xs text-muted-foreground">
                    {l.sent_at ? format(parseISO(l.sent_at), 'dd MMM · HH:mm', { locale: fr }) : '—'}
                  </td>
                  <td className="p-3">
                    <Badge className="bg-[#C9B896]/15 text-[#1F2A4A] border-[#C9B896]/30 text-[10px] uppercase">{l.kind}</Badge>
                  </td>
                  <td className="p-3 text-xs">{l.to}</td>
                  <td className="p-3 text-xs">{l.subject}</td>
                  <td className="p-3">
                    {l.ok ? (
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">✓ Envoyé</Badge>
                    ) : (
                      <span title={l.error} className="inline-block">
                        <Badge className="bg-red-50 text-red-700 border-red-200">⚠ Échec</Badge>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
